import React, { Component } from "react"
import { Navigation, Page, TaskTabPanel, Storage } from "interactive"

import direction_back from "../../images/direction/back.png"
import direction_background from "../../images/direction/background.png"
import direction_bottom from "../../images/direction/bottom.png"
import direction_front from "../../images/direction/front.png"
import direction_key from "../../images/direction/key.png"
import direction_rim from "../../images/direction/rim.png"
import direction_side from "../../images/direction/side.png"
import direction_top from "../../images/direction/top.png"
import direction from "../../images/direction/direction.jpg"

export const frontmatter = {
  title: "Direction",
  id: "id",
  figure: "1.1",
  path: "direction/",
}

function Direction() {
  return (
    <Navigation ghostMode>
      <Page>
        <h2>Lighting: Direction</h2>
        <p>
          The direction from which a light is pointed determines how
          illumination, shadow, and highlights fall on and around a subject.
        </p>
        <figure>
          <img 
            src={direction} 
            style={{ width: "100%" }} 
            alt="A light is pointed downward diagonally, shining at a downward angle. The background behind this beam of light is otherwise black." 
          />
          <figcaption>
            Photo by Jacob Joyner
          </figcaption>
        </figure>
        <p>
          The following interactive illustrates how filmmakers can manipulate
          the direction in which light is pointed to shape the way we see and
          interpret characters and situations.
        </p>
      </Page>
      <Page>
        <p>
          Depending on the context in which the lighting is used, different
          arrangements of illumination and shadow can make a character appear
          conflicted, mysterious, threatening, or glamorous.
        </p>
        <p>
          To progress to the next step, select each tab below and explore
          examples of light from different directions illuminating the same
          subject.
        </p>
        <TaskTabPanel
          key="MV1"
          state={Storage()}
          contents={[
            {
              title: "Side",
              contents: (
                <React.Fragment>
                  <img
                    src={direction_side} 
                    className="mw-100 d-block mt-3 mx-auto" 
                    alt="A photo of a person facing forward. The left side of the person’s face and upper torso is almost covered in shadow. The right side of their face and upper torso is illuminated. They are wearing a brightly colored shirt that is mostly visible." 
                  />
                  <p>
                    <br />
                    <strong>Side</strong> light neatly bisects the subject’s
                    face with light and shadow.
                  </p>
                </React.Fragment>
              ),
            },
            {
              title: "Top",
              contents: (
                <React.Fragment>
                  <img 
                    src={direction_top} 
                    className="mw-100 d-block mt-3 mx-auto" 
                    alt="A photo of a person facing forward. Much of the person’s face, neck, and arms is cast in shadow. Their forehead, nose, and brightly colored shirt are still illuminated." 
                  />
                  <p>
                    <br />
                    <strong>Top</strong> light throws the subject’s eyes into
                    shadow.
                  </p>
                </React.Fragment>
              ),
            },
            {
              title: "Bottom",
              contents: (
                <React.Fragment>
                  <img
                    src={direction_bottom}
                    className="mw-100 d-block mt-3 mx-auto"
                    alt="A photo of a person facing forward. The top of the person’s head, around their eyes, and around their collarbone are cast in shadow. Much of the rest of their face and upper torso is illuminated."
                  />
                  <p>
                    <br />
                    <strong>Bottom</strong> light (from below) reverses the natural placement of light and shadow, distorting the subject’s features. For obvious reasons, this lighting approach is often referred to as Halloween lighting.
                  </p>
                </React.Fragment>
              ),
            },
            {
              title: "Front",
              contents: (
                <React.Fragment>
                  <img 
                    src={direction_front} 
                    className="mw-100 d-block mt-3 mx-auto" 
                    alt="A photo of a person facing forward. They are almost entirely illuminated aside from some shadows under their chin." 
                  />
                  <p>
                    <br />
                    <strong>Front</strong> light eliminates most shadows,
                    flattening the subject’s features.
                  </p>
                </React.Fragment>
              ),
            },
          ]}
        />
      </Page>
      <Page>
        <p>
          Other lighting directions can help the subject stand out from the
          background, abstract her by concealing her features, or simply provide
          a natural-looking source of illumination.
        </p>
        <p>
          To complete the interactive, select each tab below and explore
          examples of light from different directions illuminating the same
          subject.
        </p>
        <TaskTabPanel
          key="MV2"
          state={Storage()}
          contents={[
            {
              title: "Background",
              contents: (
                <React.Fragment>
                  <img
                    src={direction_background}
                    className="mw-100 d-block mt-3 mx-auto"
                    alt="A photo of a person facing forward; however, the person’s features are not visible aside from their silhouette. White light appears to be behind the silhouette."
                  />
                  <p>
                    <br />
                    <strong>Background</strong> light alone throws the
                    subject into silhouette.
                  </p>
                </React.Fragment>
              ),
            },
            {
              title: "Back",
              contents: (
                <React.Fragment>
                  <img 
                    src={direction_back} 
                    className="mw-100 d-block mt-3 mx-auto" 
                    alt="A photo of a person facing forward; however, the person’s features are cast in shadow and not visible aside from the very top of their shoulders and the very top of their head." 
                  />
                  <p>
                    <br />
                    <strong>Back</strong> light and <strong>rim</strong> light outline the subject with bright highlights.
                  </p>
                </React.Fragment>
              ),
            },
            {
              title: "Rim",
              contents: (
                <React.Fragment>
                  <img 
                    src={direction_rim} 
                    className="mw-100 d-block mt-3 mx-auto" 
                    alt="A photo of a person facing forward; however, the person’s features are cast in shadow and are largely not visible aside from the outermost edge of the right side of their head, right shoulder, and right arm." 
                  />
                  <p>
                    <br />
                    <strong>Back</strong> light and <strong>rim</strong> light outline the subject with bright highlights.
                  </p>
                </React.Fragment>
              ),
            },
            {
              title: "Key",
              contents: (
                <React.Fragment>
                  <img 
                    src={direction_key} 
                    className="mw-100 d-block mt-3 mx-auto" 
                    alt="A photo of a person facing forward. They are almost entirely illuminated; however, the outermost edge of the left side of their face and the top of their left shoulder are cast in shadow." 
                  />
                  <p>
                    <br />
                    <strong>Key</strong> light is the primary source of illumination in a standard three-point lighting setup. The slightly elevated 45-degree angle provides a neutral and visually pleasing arrangement of light and shadow.
                  </p>
                </React.Fragment>
              ),
            },
          ]}
        />
      </Page>
    </Navigation>
  )
}

export default Direction
